import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getByContentData } from "slices/discovery/actions/byContent";
import { setByContentCurrentPage } from "slices/discovery/discoverySlice";
import ByContentHeader from "layout/discovery/document/ByContent/ByContentHeader";
import { getMaxFreq } from "utils/discovery";
import { isExcel, scrollToTop } from "utils";
import { ITEMS_PER_PAGE } from "constants/app";
import BodyRow from "layout/discovery/document/ByContent/BodyRow";
import IgnoreList from "layout/discovery/ignoreList";
import Pagination from "@visiblethread/react-components/dist/components/Pagination";
import { elements } from "constants/types";
import { useColumnsResizing } from "hooks/generic/useColumnsResizing";

const MIN_WIDTHS = [29, 65, 65, 350, 650];
const INIT_WIDTHS = [29, 80, 80, "auto", 650];
const SS_KEY_DOCUMENT_BY_CONTENT = "discovery-by-content-settings";

const ByContent = () => {
  const dispatch = useDispatch();
  // To correctly init resizing logic, need to trigger rerender when header ref is present
  const [headerRef, setHeaderRef] = useState(null);
  const headerRefMemoized = useMemo(
    () => ({ current: headerRef }),
    [headerRef]
  );
  const bodyRef = useRef(null);

  const data = useSelector((state) => state.discovery.byContent);
  const [displayedData, setDisplayedData] = useState([]);
  const maxFreq = useMemo(() => getMaxFreq(data || []), [data]);
  const [limits, setLimits] = useState({ from: 0, to: ITEMS_PER_PAGE + 1 });
  const pageNumber = useSelector(
    (state) => state.discovery.config.byContentPage
  );
  const configLoading = useSelector((state) => state.discovery.config.loading);
  const isLoading =
    configLoading?.find(
      (load) =>
        load.element === elements.BY_CONTENT_VIEW && load.loading === true
    ) !== undefined;
  const excel = isExcel(
    useSelector((state) => state.discovery.themes[0]?.Documents[0].Name)
  );

  const { columnWidths, onColumnResize } = useColumnsResizing(
    INIT_WIDTHS,
    MIN_WIDTHS,
    SS_KEY_DOCUMENT_BY_CONTENT,
    headerRefMemoized
  );

  useEffect(() => {
    if (data?.length <= 0) dispatch(getByContentData(ITEMS_PER_PAGE));
  }, []);

  /* Update displayed data either if page was changed or if data was changed (user removed item) */
  useEffect(() => {
    if (!data) return;

    setDisplayedData([...data].slice(limits.from, limits.to));
  }, [limits, data]);

  const handlePageChange = ([from, to]) => {
    setLimits({ from, to: to + 1 });
    const newPageNumber = Math.ceil(from / ITEMS_PER_PAGE) + 1;
    dispatch(setByContentCurrentPage(newPageNumber));
    scrollToTop(bodyRef);
  };

  const handleScroll = (e) => {
    if (headerRef.current && bodyRef.current) {
      const { scrollLeft } = e.target;
      headerRef.current.scrollLeft = scrollLeft;
    }
  };

  if (excel) return null;

  return (
    <div
      className="by_content"
      role="presentation"
      data-testid="by-content-pane"
    >
      {data?.length > 0 && (
        <>
          <div className="by_content__title">
            <h3>Detail</h3>
          </div>
          <IgnoreList />
          <div className="by_content__pagination_wrapper">
            <Pagination
              numberOfItems={data.length}
              itemsPerPage={ITEMS_PER_PAGE}
              initialPage={pageNumber}
              onPageChange={handlePageChange}
              loadingConfig={{
                loading: isLoading,
                message: "Calculating...",
              }}
            />
          </div>
          <ByContentHeader
            columnWidths={columnWidths}
            onResize={onColumnResize}
            headerRef={setHeaderRef}
          />
          <div
            ref={bodyRef}
            className="by_content__body"
            onScroll={handleScroll}
          >
            {displayedData.map((item) => (
              <BodyRow
                item={item}
                maxFreq={maxFreq}
                columnWidths={columnWidths}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ByContent;
